/**
 * Collection Workflow Tab for NCR Edit
 *
 * Manages the collection process workflow including status stepper,
 * initiation form, and status progression actions.
 */

import { useState } from 'react';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import {
  Package, Clock, Truck, CheckCircle2, ArrowRight, Loader2,
} from 'lucide-react';
import type { NonConformanceReport as NCRType } from '../../lib/api';
import { useFormatters } from '../../contexts/PreferencesContext';

// =========================================================================
// Collection Workflow Status Stepper
// =========================================================================

const COLLECTION_STEPS = [
  { key: 'requested', label: 'Requested', icon: Clock },
  { key: 'pending', label: 'Pending', icon: Package },
  { key: 'confirmed', label: 'Confirmed', icon: Truck },
  { key: 'completed', label: 'Completed', icon: CheckCircle2 },
] as const;

function CollectionStatusStepper({ status }: { status: string }) {
  const currentIdx = COLLECTION_STEPS.findIndex(s => s.key === status);
  return (
    <div className="flex items-center gap-1 w-full">
      {COLLECTION_STEPS.map((step, idx) => {
        const Icon = step.icon;
        const isCompleted = idx < currentIdx;
        const isCurrent = idx === currentIdx;
        return (
          <div key={step.key} className="flex items-center flex-1">
            <div className="flex flex-col items-center flex-1">
              <div className={`h-8 w-8 rounded-full flex items-center justify-center text-xs font-medium ${
                isCompleted ? 'bg-green-100 text-green-700' :
                isCurrent ? 'bg-blue-100 text-blue-700 ring-2 ring-blue-300' :
                'bg-gray-100 text-gray-400'
              }`}>
                <Icon className="h-4 w-4" />
              </div>
              <span className={`text-[10px] mt-1 ${
                isCompleted ? 'text-green-700 font-medium' :
                isCurrent ? 'text-blue-700 font-medium' :
                'text-gray-400'
              }`}>{step.label}</span>
            </div>
            {idx < COLLECTION_STEPS.length - 1 && (
              <div className={`h-0.5 flex-1 mx-1 ${
                idx < currentIdx ? 'bg-green-300' : 'bg-gray-200'
              }`} />
            )}
          </div>
        );
      })}
    </div>
  );
}

// =========================================================================
// Collection Workflow Tab
// =========================================================================

export interface CollectionWorkflowTabProps {
  ncr: NCRType;
  isSupplier: boolean;
  isCustomer: boolean;
  saving: boolean;
  onInitiate: (data: { proposed_collection_date?: string; collection_address?: string; collection_notes?: string; supplier_notes?: string }) => void;
  onUpdateStatus: (status: string, data?: Record<string, unknown>) => void;
}

export function CollectionWorkflowTab({ ncr, isSupplier, isCustomer, saving, onInitiate, onUpdateStatus }: CollectionWorkflowTabProps) {
  const { formatDate } = useFormatters();
  const [initForm, setInitForm] = useState({ proposed_collection_date: '', collection_address: '', supplier_notes: '' });
  const [confirmForm, setConfirmForm] = useState({ confirmed_collection_date: '', tracking_reference: '' });
  const [completionNotes, setCompletionNotes] = useState('');

  const process = ncr.collection_process;

  if (!ncr.is_collection_required) {
    return (
      <Card>
        <CardContent className="pt-6">
          <div className="text-center py-8 text-muted-foreground">
            Collection is not required for this NCR.
          </div>
        </CardContent>
      </Card>
    );
  }

  // If no process exists yet, show initiation form
  if (!process) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Package className="h-5 w-5" />
            Initiate Collection
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-sm text-muted-foreground">
            Defective parts need to be collected/returned. Start the collection process below.
          </p>
          {isSupplier ? (
            <>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Proposed Collection Date</Label>
                  <Input
                    type="date"
                    value={initForm.proposed_collection_date}
                    onChange={e => setInitForm(f => ({ ...f, proposed_collection_date: e.target.value }))}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Collection Address</Label>
                  <Input
                    value={initForm.collection_address}
                    onChange={e => setInitForm(f => ({ ...f, collection_address: e.target.value }))}
                    placeholder="Where should parts be collected from?"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Notes</Label>
                <Textarea
                  value={initForm.supplier_notes}
                  onChange={e => setInitForm(f => ({ ...f, supplier_notes: e.target.value }))}
                  placeholder="Instructions for the supplier..."
                  rows={3}
                />
              </div>
              <Button onClick={() => onInitiate(initForm)} disabled={saving}>
                {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Package className="h-4 w-4 mr-2" />}
                Initiate Collection
              </Button>
            </>
          ) : (
            <p className="text-sm text-amber-600">Waiting for the customer to initiate the collection process.</p>
          )}
        </CardContent>
      </Card>
    );
  }

  // Process exists - show status stepper and actions
  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Package className="h-5 w-5" />
          Collection Process
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        <CollectionStatusStepper status={process.status} />

        {/* Process details */}
        <div className="grid grid-cols-2 gap-4 text-sm">
          <div>
            <span className="text-muted-foreground">Requested by:</span>{' '}
            <span className="font-medium">{process.requested_by_name}</span>
          </div>
          <div>
            <span className="text-muted-foreground">Requested:</span>{' '}
            <span className="font-medium">{formatDate(process.requested_date)}</span>
          </div>
          {process.proposed_collection_date && (
            <div>
              <span className="text-muted-foreground">Proposed date:</span>{' '}
              <span className="font-medium">{formatDate(process.proposed_collection_date)}</span>
            </div>
          )}
          {process.confirmed_collection_date && (
            <div>
              <span className="text-muted-foreground">Confirmed date:</span>{' '}
              <span className="font-medium">{formatDate(process.confirmed_collection_date)}</span>
            </div>
          )}
          {process.tracking_reference && (
            <div>
              <span className="text-muted-foreground">Tracking:</span>{' '}
              <span className="font-medium">{process.tracking_reference}</span>
            </div>
          )}
          {process.actual_collection_date && (
            <div>
              <span className="text-muted-foreground">Actual collection:</span>{' '}
              <span className="font-medium">{formatDate(process.actual_collection_date)}</span>
            </div>
          )}
          {process.collection_address && (
            <div className="col-span-2">
              <span className="text-muted-foreground">Address:</span>{' '}
              <span className="font-medium">{process.collection_address}</span>
            </div>
          )}
        </div>

        {/* Notes display */}
        {(process.supplier_notes || process.collection_notes || process.customer_receipt_notes) && (
          <div className="space-y-2 border-t pt-4">
            {process.supplier_notes && (
              <div className="text-sm"><span className="text-muted-foreground">Customer notes:</span> {process.supplier_notes}</div>
            )}
            {process.collection_notes && (
              <div className="text-sm"><span className="text-muted-foreground">Collection notes:</span> {process.collection_notes}</div>
            )}
            {process.customer_receipt_notes && (
              <div className="text-sm"><span className="text-muted-foreground">Supplier receipt notes:</span> {process.customer_receipt_notes}</div>
            )}
          </div>
        )}

        {/* Actions based on current status */}
        {process.status === 'requested' && isSupplier && (
          <div className="border-t pt-4">
            <Button onClick={() => onUpdateStatus('pending')} disabled={saving}>
              <ArrowRight className="h-4 w-4 mr-2" />
              Move to Pending
            </Button>
          </div>
        )}

        {process.status === 'pending' && isCustomer && (
          <div className="border-t pt-4 space-y-4">
            <h4 className="font-medium text-sm">Confirm Collection Date</h4>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Confirmed Collection Date</Label>
                <Input
                  type="date"
                  value={confirmForm.confirmed_collection_date}
                  onChange={e => setConfirmForm(f => ({ ...f, confirmed_collection_date: e.target.value }))}
                />
              </div>
              <div className="space-y-2">
                <Label>Tracking Reference</Label>
                <Input
                  value={confirmForm.tracking_reference}
                  onChange={e => setConfirmForm(f => ({ ...f, tracking_reference: e.target.value }))}
                  placeholder="Shipment tracking number"
                />
              </div>
            </div>
            <Button onClick={() => onUpdateStatus('confirmed', confirmForm)} disabled={saving}>
              <CheckCircle2 className="h-4 w-4 mr-2" />
              Confirm Date
            </Button>
          </div>
        )}

        {process.status === 'confirmed' && isSupplier && (
          <div className="border-t pt-4 space-y-4">
            <h4 className="font-medium text-sm">Complete Collection</h4>
            <div className="space-y-2">
              <Label>Receipt Notes (optional)</Label>
              <Textarea
                value={completionNotes}
                onChange={e => setCompletionNotes(e.target.value)}
                placeholder="Confirm parts received in good order..."
                rows={2}
              />
            </div>
            <Button onClick={() => onUpdateStatus('completed', { customer_receipt_notes: completionNotes })} disabled={saving}>
              <CheckCircle2 className="h-4 w-4 mr-2" />
              Mark as Completed
            </Button>
          </div>
        )}

        {process.status === 'completed' && (
          <div className="flex items-center gap-3 p-4 bg-green-50 rounded-lg border border-green-200">
            <CheckCircle2 className="h-5 w-5 text-green-600" />
            <div>
              <h3 className="font-semibold text-green-800">Collection Completed</h3>
              {process.completed_date && (
                <p className="text-sm text-green-600">
                  Completed on {formatDate(process.completed_date)}
                  {process.completed_by_name && ` by ${process.completed_by_name}`}
                </p>
              )}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
